import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { NameOnCard } from "#/lib/carrot-api/types/NameOnCard"
import { NAME_MODIFIED } from "#/pages/request-carrot-card-flow/workflows/steps"
import { attemptRequest } from "../AirwallexUserAgreement/agreement-helpers"

const nameOnCardQueryKey = ["nameOnCard"]
const fetchNameOnCard = () => carrotClient.getNameOnCard()

export function useNameOnCard() {
  return useQuery(nameOnCardQueryKey, fetchNameOnCard)
}

// Names we can't convert to English characters still go straight to the request so the member sees the error
export function shouldReviewNameOnCard(nameOnCard: NameOnCard | null | undefined) {
  return Boolean(nameOnCard?.nameWasModified && nameOnCard.isSupported)
}

/**
 * Submit handler for the Airwallex card request. If the name we'll share with Airwallex differs from the
 * member's name on file, routes to the name review step instead of requesting the card.
 */
export function useAirwallexCardRequestSubmit(requestCard: () => Promise<unknown>) {
  const { send } = useStateMachine()
  const queryClient = useQueryClient()
  const isActive = useRef(true)

  useEffect(() => {
    isActive.current = true
    return () => {
      isActive.current = false
    }
  }, [])

  return async () => {
    let nameOnCard: NameOnCard | null = null
    try {
      nameOnCard = await queryClient.fetchQuery({
        queryKey: nameOnCardQueryKey,
        queryFn: fetchNameOnCard
      })
    } catch {
      // The preview is informational; the backend normalizes the name either way
    }

    if (!isActive.current) return

    if (shouldReviewNameOnCard(nameOnCard)) {
      send(NAME_MODIFIED)
      return
    }
    await attemptRequest(requestCard)
  }
}
